import type { AccountCoverage, CoverageTier } from '../../types/reporting';

// Pure view logic for the Coverage ops table — range/day-count formatting, gap
// detection (days materially < span), and sorting. All total + unit-tested; the
// page just renders these.

// Inclusive calendar-day span between two YYYY-MM-DD dates (from == to → 1 day).
// Returns 0 for an invalid / out-of-order pair (defensive; never negative).
export function spanDays(from: string, to: string): number {
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return 0;
  return Math.round((end - start) / 86_400_000) + 1;
}

// A tier is GAPPED when its distinct-day count is materially less than its
// from→to span (an un-refilled backfill chunk, a mid-range hole). "Materially" =
// more than one missing day, so a single boundary-day rounding difference never
// false-flags. Null tier → not gapped (nothing to flag).
export function tierIsGapped(tier: CoverageTier | null): boolean {
  return tierMissingDays(tier) > 1;
}

// How many calendar days inside the range have NO data (span − days, floored at
// 0). 0 = contiguous full coverage. Drives the gap note + tooltip.
export function tierMissingDays(tier: CoverageTier | null): number {
  if (!tier) return 0;
  return Math.max(0, spanDays(tier.from, tier.to) - tier.days);
}

// A "gap" almost always just means the account wasn't DELIVERING ads on those
// days (paused / between flights / zero-spend) — normal, not missing data. So it
// surfaces as a quiet, muted FACT, not a warning, and only above a small
// threshold: a couple of missing days is boundary/rounding noise (the >1
// tierIsGapped guard drifting into it), so we don't surface it at all. A genuine
// pull-gap (a big hole) still stands out as a large count without any alarm.
export const GAP_NOTE_THRESHOLD = 2;

// The short muted label for a tier's no-activity days (e.g. "13 days no
// activity"), or null when the gap is trivial (≤ threshold) or the tier is
// absent. Reuses tierMissingDays.
export function tierGapNote(tier: CoverageTier | null): string | null {
  const missing = tierMissingDays(tier);
  if (missing <= GAP_NOTE_THRESHOLD) return null;
  return `${missing} days no activity`;
}

// The hover explanation carrying the exact count + the honest meaning (we can't
// distinguish "no delivery" from "never pulled" — it's usually the former). Null
// when there's no note to explain.
export function tierGapTooltip(tier: CoverageTier | null): string | null {
  const missing = tierMissingDays(tier);
  if (missing <= GAP_NOTE_THRESHOLD) return null;
  return `${missing} calendar days in this range have no data — usually days the account had no ads delivering.`;
}

// Compact tier label, e.g. "2024-01-04 → 2026-07-04 · 883d". A null tier → "—".
export function formatCoverageRange(tier: CoverageTier | null): string {
  if (!tier) return '—';
  return `${tier.from} → ${tier.to} · ${tier.days}d`;
}

// ---------------------------------------------------------------------------
// Per-tier pull-status — "what's been run vs still needed", at a glance. Derived
// from HOW FAR BACK the data goes (the tier's earliest-date age), so it reflects
// reality regardless of whether the pull was CLI- or UI-triggered.
// ---------------------------------------------------------------------------

// The age (in days ago) at which a tier's earliest date flips from "recent-only"
// (a ~90-day pull, whose earliest date sits ~90 days back) to "deep backfill" (the
// ~25-month / ~760-day pull). 120 days cleanly separates the two with margin for
// pull latency / partial windows. HEURISTIC — tune if a real case sits near it.
export const DEEP_BACKFILL_AGE_DAYS = 120;

export type StructuralPullStatus = 'none' | 'recent' | 'full';
export type DemographicsPullStatus = 'none' | 'recent' | 'full';

// Whole days between a YYYY-MM-DD date and `nowMs` (floored, never negative). An
// unparseable date → 0 (treated as today; the caller only distinguishes the >120
// boundary, so a garbage date defaults to the "recent" bucket, never "full").
function ageInDays(from: string, nowMs: number): number {
  const start = Date.parse(from);
  if (Number.isNaN(start)) return 0;
  return Math.max(0, Math.floor((nowMs - start) / 86_400_000));
}

// Ad-structural pull status: no data / recent-only (earliest ≲ ~120d ago, the
// 90-day pull) / full (earliest > ~120d ago, the 25mo deep backfill). Rendered as
// a chip alongside the range. `nowMs` defaults to now (injectable for tests).
export function structuralPullStatus(
  tier: CoverageTier | null,
  nowMs: number = Date.now(),
): StructuralPullStatus {
  if (!tier) return 'none';
  return ageInDays(tier.from, nowMs) > DEEP_BACKFILL_AGE_DAYS ? 'full' : 'recent';
}

// Demographics pull status: same shape / same threshold (none / recent = the small
// pull / full = the deep pull).
export function demographicsPullStatus(
  tier: CoverageTier | null,
  nowMs: number = Date.now(),
): DemographicsPullStatus {
  if (!tier) return 'none';
  return ageInDays(tier.from, nowMs) > DEEP_BACKFILL_AGE_DAYS ? 'full' : 'recent';
}

// Human labels for the chips. Ad-structural's "recent" reads as "90-day" (the pull
// that produced it); demographics' as "Recent".
export function structuralPullStatusLabel(s: StructuralPullStatus): string {
  return s === 'none' ? 'None' : s === 'recent' ? '90-day' : 'Full (25mo)';
}

export function demographicsPullStatusLabel(s: DemographicsPullStatus): string {
  return s === 'none' ? 'None' : s === 'recent' ? 'Recent' : 'Full';
}

// ---------------------------------------------------------------------------
// #1 — bounded member-list summary. A shared row lists every member location, but
// the ACCOUNT column is width-capped, so the list is truncated ("A, B, +3 more")
// with the full set on hover. Pure; the page renders it into a fixed-width cell.
// ---------------------------------------------------------------------------

export function summarizeLocations(names: readonly string[], max = 2): string {
  if (names.length <= max) return names.join(', ');
  const shown = names.slice(0, max).join(', ');
  return `${shown}, +${names.length - max} more`;
}

// ---------------------------------------------------------------------------
// #2 — "Needs full backfill" (the onboarding worklist's core signal). Keyed off
// the pull-status TIER, not off NONE — so it stays true through a partial ingest
// (the 2h --days=14 cron / a partial Zernio ingest turns NONE → RECENT/90-DAY,
// which is still "not fully backfilled"). An account is reporting-eligible by the
// time it reaches the Coverage roster (the server filters via isReportingEligible),
// so the check is purely tier depth: NOT-FULL on structural OR demographics.
//
// The brief's literal wording is "structural below FULL, or demographics absent";
// we use "demographics below FULL" (a superset that still catches absent = NONE)
// so a 90-day/recent demographics pull — which genuinely still needs the 25-month
// pull — is also flagged. FLAGGED: the slightly-wider-than-literal reading.
// ---------------------------------------------------------------------------

export function needsFullBackfill(row: AccountCoverage, nowMs: number = Date.now()): boolean {
  return (
    structuralPullStatus(row.adStructural, nowMs) !== 'full' ||
    demographicsPullStatus(row.demographics, nowMs) !== 'full'
  );
}

// ---------------------------------------------------------------------------
// #3 — connection recency (the onboarding worklist's "who just got connected").
// Reads AccountCoverage.metaConnectedAt (the server's earliest first-Meta-connect
// across the ad account's members; null = legacy / linked outside the wizard).
// ---------------------------------------------------------------------------

// Whole days since the connect (floored, never negative), or null when unknown /
// unparseable. Drives both the "connected in last N days" filter + the column.
export function connectedAgeDays(iso: string | null, nowMs: number = Date.now()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((nowMs - t) / 86_400_000));
}

// True when the row was Meta-connected within the last `days` (inclusive). A null
// / unknown connect time is NOT recent (legacy accounts fail the recency filter).
export function connectedWithinDays(
  row: AccountCoverage,
  days: number,
  nowMs: number = Date.now(),
): boolean {
  const age = connectedAgeDays(row.metaConnectedAt, nowMs);
  return age !== null && age <= days;
}

// Compact recency label for the Connected column: "today" / "3d ago" / "5w ago" /
// "4mo ago", or "—" when unknown. Weeks/months keep it scannable for older rows.
export function formatConnectedAge(iso: string | null, nowMs: number = Date.now()): string {
  const age = connectedAgeDays(iso, nowMs);
  if (age === null) return '—';
  if (age === 0) return 'today';
  if (age < 14) return `${age}d ago`;
  if (age < 60) return `${Math.floor(age / 7)}w ago`;
  return `${Math.floor(age / 30)}mo ago`;
}

// ---------------------------------------------------------------------------
// #4 — gap magnitude + severity (the "worst gaps / missing-days" surfacing). The
// per-account gap rolls up the WORST (max) missing-day count across the three
// tiers (each tier's span − distinct-days; see tierMissingDays).
//
// HEURISTIC — FLAGGED: we can only see each tier's from/to + distinct-day COUNT,
// NOT the actual missing dates, so we CANNOT tell an interior hole (suspicious =
// likely a missing pull) from trailing/leading no-spend (usually genuine). So the
// severity never hard-claims "missing data"; the reliable "needs attention" signal
// the brief asks for is a LARGE gap COMBINED WITH a sub-FULL structural tier (a
// fully-backfilled account with a big gap is treated as genuine no-spend and never
// escalates to red). Amber = a sizeable gap worth a glance; muted = small (usually
// no-spend); red = large gap on a not-fully-pulled account (probably a real hole).
// ---------------------------------------------------------------------------

export const GAP_WARN_THRESHOLD = 30; // amber — a look-worth gap
export const GAP_ALERT_THRESHOLD = 90; // red (only with a sub-FULL tier)

export type GapSeverity = 'none' | 'notice' | 'warn' | 'alert';

// The worst missing-day count across the row's tiers (0 when every tier is
// contiguous / absent). Sort key + the severity input.
export function coverageGapDays(row: AccountCoverage): number {
  return Math.max(
    tierMissingDays(row.campaign),
    tierMissingDays(row.adStructural),
    tierMissingDays(row.demographics),
  );
}

export function gapSeverity(row: AccountCoverage, nowMs: number = Date.now()): GapSeverity {
  const gap = coverageGapDays(row);
  if (gap <= GAP_NOTE_THRESHOLD) return 'none'; // trivial — boundary/rounding noise
  const notFullyPulled = structuralPullStatus(row.adStructural, nowMs) !== 'full';
  if (gap > GAP_ALERT_THRESHOLD && notFullyPulled) return 'alert'; // big + sub-full → likely a real hole
  if (gap > GAP_WARN_THRESHOLD) return 'warn'; // sizeable — worth a glance
  return 'notice'; // small — usually genuine no-spend
}

// ---------------------------------------------------------------------------
// Sorting — by account name, connection recency, worst-gap magnitude, or any
// tier's start / end / day-count.
// ---------------------------------------------------------------------------

export type CoverageTierKey = 'campaign' | 'adStructural' | 'demographics';
export type CoverageSortField =
  | 'account'
  | 'connected'
  | 'gap'
  | `${CoverageTierKey}.${'from' | 'to' | 'days'}`;
export type SortDir = 'asc' | 'desc';

export interface CoverageSort {
  field: CoverageSortField;
  dir: SortDir;
}

// A missing tier always sorts to the BOTTOM regardless of direction (a "—" row is
// least interesting either way). Account sort is by name (id fallback), locale-aware.
function tierOf(row: AccountCoverage, key: CoverageTierKey): CoverageTier | null {
  return row[key];
}

function compareBy(a: AccountCoverage, b: AccountCoverage, field: CoverageSortField): number {
  if (field === 'account') {
    const an = (a.accountName ?? a.accountId).toLowerCase();
    const bn = (b.accountName ?? b.accountId).toLowerCase();
    return an < bn ? -1 : an > bn ? 1 : 0;
  }
  if (field === 'gap') {
    return coverageGapDays(a) - coverageGapDays(b);
  }
  if (field === 'connected') {
    // Null connect times are pinned to the bottom by sortCoverage, so both are
    // present here. asc = oldest first; the page defaults it to desc (newest first).
    return Date.parse(a.metaConnectedAt!) - Date.parse(b.metaConnectedAt!);
  }
  const [key, prop] = field.split('.') as [CoverageTierKey, 'from' | 'to' | 'days'];
  const ta = tierOf(a, key);
  const tb = tierOf(b, key);
  // Null tiers sink to the bottom (return before applying direction).
  if (!ta && !tb) return 0;
  if (!ta) return 1;
  if (!tb) return -1;
  if (prop === 'days') return ta.days - tb.days;
  return ta[prop] < tb[prop] ? -1 : ta[prop] > tb[prop] ? 1 : 0;
}

// Whether a row has NO value for the sorted dimension (a null tier, or an unknown
// connect time) — such rows pin to the bottom regardless of direction. 'account'
// and 'gap' always have a value (name fallback / numeric 0), so they never pin.
function pinnedForSort(row: AccountCoverage, field: CoverageSortField): boolean {
  if (field === 'connected') return row.metaConnectedAt == null;
  if (field === 'account' || field === 'gap') return false;
  const [key] = field.split('.') as [CoverageTierKey];
  return !tierOf(row, key);
}

// Sort a copy of the rows. Rows with no value for the sorted dimension (null tiers,
// unknown connect times) stay pinned to the bottom — their pin returns a fixed
// +1/−1 that we DON'T invert for desc, so toggling direction never floats the
// empty rows up.
export function sortCoverage(rows: AccountCoverage[], sort: CoverageSort): AccountCoverage[] {
  return [...rows].sort((a, b) => {
    const na = pinnedForSort(a, sort.field);
    const nb = pinnedForSort(b, sort.field);
    if (na && nb) return 0;
    if (na) return 1; // a has no value → bottom
    if (nb) return -1; // b has no value → bottom
    const base = compareBy(a, b, sort.field);
    return sort.dir === 'asc' ? base : -base;
  });
}

// ---------------------------------------------------------------------------
// Text filter — account or brand name substring (case-insensitive).
// ---------------------------------------------------------------------------

// Scans the row's FULL location list + group + brand + title — NOT just the
// displayed title. Scott's hard requirement: searching a covered location (e.g.
// "Barrington") must surface the shared row even though its title shows the GROUP
// name. So every member location name is searchable, not just the standalone title.
export function filterCoverage(rows: AccountCoverage[], query: string): AccountCoverage[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter(
    (r) =>
      r.locationNames.some((n) => n.toLowerCase().includes(q)) ||
      (r.groupName ?? '').toLowerCase().includes(q) ||
      (r.accountName ?? '').toLowerCase().includes(q) ||
      (r.brandName ?? '').toLowerCase().includes(q) ||
      r.adAccountId.toLowerCase().includes(q),
  );
}

// ---------------------------------------------------------------------------
// Control-panel facet filters (Phase 2) — the "who's had what" worklist. Each is
// a tri-state: 'all' (no constraint) / on-or-off (daily sync) / yes-or-no (has a
// tier). Composed with the text filter by filterCoverageRows.
// ---------------------------------------------------------------------------

export type SyncFilter = 'all' | 'on' | 'off';
export type HasFilter = 'all' | 'yes' | 'no';
// Pull-status facets: 'all' = no constraint, else match that exact status.
export type StructuralPullFilter = 'all' | StructuralPullStatus;
export type DemographicsPullFilter = 'all' | DemographicsPullStatus;
// #2 — the rolled-up onboarding-worklist facet: 'needs-full' (still needs the 25mo
// pull on some tier) / 'complete' (fully backfilled). 'all' = no constraint.
export type BackfillFilter = 'all' | 'needs-full' | 'complete';
// #3 — connection recency: connected within the last 7 / 30 days. 'all' = any.
export type ConnectedFilter = 'all' | '7d' | '30d';
// #4 — worst-gap magnitude: > 30 / > 90 missing days on some tier. 'all' = any.
export type GapFilter = 'all' | 'gt30' | 'gt90';

export interface CoverageFilters {
  text: string;
  dailySync: SyncFilter;
  adStructural: HasFilter; // has an ad-structural range?
  demographics: HasFilter; // has a demographics range?
  // "Who still needs what" — filter by the per-tier pull depth (None → first pull
  // needed, recent/90-day → still needs the 25mo, full → done).
  adStructuralPull: StructuralPullFilter;
  demographicsPull: DemographicsPullFilter;
  // #2 — the rolled-up "needs a full backfill" worklist filter (survives partial
  // ingest, keyed off the tier not off NONE).
  backfill: BackfillFilter;
  // #3 — "connected in the last N days" (fresh onboarding).
  connected: ConnectedFilter;
  // #4 — "worst gap over N days" (missing-days alert worklist).
  gap: GapFilter;
}

export const EMPTY_COVERAGE_FILTERS: CoverageFilters = {
  text: '',
  dailySync: 'all',
  adStructural: 'all',
  demographics: 'all',
  adStructuralPull: 'all',
  demographicsPull: 'all',
  backfill: 'all',
  connected: 'all',
  gap: 'all',
};

function matchesSync(row: AccountCoverage, f: SyncFilter): boolean {
  if (f === 'on') return row.reportingSyncEnabled;
  if (f === 'off') return !row.reportingSyncEnabled;
  return true;
}

function matchesHas(tier: CoverageTier | null, f: HasFilter): boolean {
  if (f === 'yes') return tier !== null;
  if (f === 'no') return tier === null;
  return true;
}

function matchesBackfill(row: AccountCoverage, f: BackfillFilter, nowMs: number): boolean {
  if (f === 'needs-full') return needsFullBackfill(row, nowMs);
  if (f === 'complete') return !needsFullBackfill(row, nowMs);
  return true;
}

function matchesConnected(row: AccountCoverage, f: ConnectedFilter, nowMs: number): boolean {
  if (f === '7d') return connectedWithinDays(row, 7, nowMs);
  if (f === '30d') return connectedWithinDays(row, 30, nowMs);
  return true;
}

function matchesGap(row: AccountCoverage, f: GapFilter): boolean {
  if (f === 'gt30') return coverageGapDays(row) > GAP_WARN_THRESHOLD;
  if (f === 'gt90') return coverageGapDays(row) > GAP_ALERT_THRESHOLD;
  return true;
}

// Does one row pass the facet filters (daily-sync + has-ad-structural + has-
// demographics + the per-tier pull-status)? Text is applied separately
// (filterCoverageRows). Pure; `nowMs` feeds the pull-status classification.
export function matchesCoverageFilters(
  row: AccountCoverage,
  f: CoverageFilters,
  nowMs: number = Date.now(),
): boolean {
  return (
    matchesSync(row, f.dailySync) &&
    matchesHas(row.adStructural, f.adStructural) &&
    matchesHas(row.demographics, f.demographics) &&
    (f.adStructuralPull === 'all' ||
      structuralPullStatus(row.adStructural, nowMs) === f.adStructuralPull) &&
    (f.demographicsPull === 'all' ||
      demographicsPullStatus(row.demographics, nowMs) === f.demographicsPull) &&
    matchesBackfill(row, f.backfill, nowMs) &&
    matchesConnected(row, f.connected, nowMs) &&
    matchesGap(row, f.gap)
  );
}

// Apply the text filter + the facet filters together. Pure.
export function filterCoverageRows(
  rows: AccountCoverage[],
  f: CoverageFilters,
  nowMs: number = Date.now(),
): AccountCoverage[] {
  return filterCoverage(rows, f.text).filter((r) => matchesCoverageFilters(r, f, nowMs));
}

// True when ANY row has a pull in flight — drives the page's faster poll while
// a backfill runs (so the running annotation + re-enabled triggers update live).
export function anyCoverageRunning(rows: readonly AccountCoverage[]): boolean {
  return rows.some((r) => r.activeBackfill !== null);
}
